export const OrderCorrectionTarget = {
  Shopify: 'SHOPIFY',
  ProtheusCustomer: 'PROTHEUS_CUSTOMER',
  Reprocess: 'REPROCESS',
  InvoiceDetailsRefresh: 'INVOICE_DETAILS_REFRESH',
  InvoiceDetailsRefreshAndTransmit: 'INVOICE_DETAILS_REFRESH_AND_TRANSMIT',
  None: 'NONE'
} as const;

export type OrderCorrectionTargetType =
  typeof OrderCorrectionTarget[keyof typeof OrderCorrectionTarget];

export const OrderCorrectionDiagnosticState = {
  ActionRequired: 'action_required',
  WaitingRetry: 'waiting_retry',
  ManualReview: 'manual_review',
  NoAction: 'no_action',
  NotFound: 'not_found'
} as const;

export type OrderCorrectionDiagnosticStateType =
  typeof OrderCorrectionDiagnosticState[keyof typeof OrderCorrectionDiagnosticState] | string;

export interface OrderCorrectionGatewayError {
  code: string;
  message: string;
}

/**
 * Envelope devolvido pelo BFF Sislog. `data` pode acompanhar uma falha, por
 * exemplo quando uma operacao precisa ser reconciliada.
 */
export interface OrderCorrectionGatewayResponse<TData> {
  success: boolean;
  data?: TData | null;
  error?: OrderCorrectionGatewayError | null;
  requestId?: string;
  statusCode: number;
}

export interface OrderCorrectionDiagnosticsBatch {
  items: OrderCorrectionDiagnosticItem[];
  summary: OrderCorrectionDiagnosticsSummary;
  evaluatedAt: string;
}

export interface OrderCorrectionDiagnosticsSummary {
  requested: number;
  actionRequired: number;
  waitingRetry: number;
  manualReview: number;
  noAction: number;
  notFound: number;
  ambiguous: number;
}

export interface OrderCorrectionDiagnosticItem {
  orderNumber: number;
  state: OrderCorrectionDiagnosticStateType;
  lifecycle: string;
  found: boolean;
  ambiguous: boolean;
  createdOnProtheus: boolean;
  invoiced: boolean;
  integrationStatus?: string | null;
  attempts: OrderCorrectionRetryInfo;
  alert: OrderCorrectionDiagnosticAlert;
  availableActions: OrderCorrectionAvailableAction[];
  diagnosticVersion: string;
  evaluatedAt: string;
}

export interface OrderCorrectionRetryInfo {
  currentTransactionAttempts: number;
  historicalAttempts: number;
  totalAttempts: number;
  maxAttempts?: number | null;
  lastAttemptAt?: string | null;
  nextAttemptAt?: string | null;
  automaticRetryAvailable: boolean;
}

export interface OrderCorrectionDiagnosticAlert {
  code: string;
  category: string;
  severity: string;
  message: string;
  suggestion: string;
  technicalMessage?: string | null;
  affectedFields: string[];
}

export interface OrderCorrectionAvailableAction {
  code: string;
  target: OrderCorrectionTargetType | string;
  requiresPreview: boolean;
  editableFields: string[];
}

export interface OrderCorrectionForm {
  orderNumber: number;
  diagnosticVersion: string;
  state: OrderCorrectionDiagnosticStateType;
  shopify?: OrderCorrectionSourceData | null;
  protheus?: OrderCorrectionSourceData | null;
  availableActions: OrderCorrectionAvailableAction[];
  warnings: string[];
}

export interface OrderCorrectionSourceData {
  source: string;
  version: string;
  cpfCnpjMasked?: string | null;
  address: OrderCorrectionAddressData;
}

export interface OrderCorrectionAddressData {
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  complement?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  countryCode?: string | null;
}

/** Null/undefined preserva o valor; string vazia limpa apenas campos opcionais. */
export interface OrderCorrectionAddressPatch {
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  complement?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  countryCode?: string | null;
}

export interface OrderCorrectionCommand {
  diagnosticVersion: string;
  reason: string;
  cpfCnpj?: string | null;
  address?: OrderCorrectionAddressPatch | null;
}

export interface OrderCorrectionExecuteCommand extends OrderCorrectionCommand {
  previewId: string;
}

export interface OrderCorrectionReprocessCommand {
  diagnosticVersion: string;
  reason: string;
  recoveryToken?: string;
}

export interface OrderCorrectionReprocessPreparation {
  recoveryToken: string;
  expiresAt: string;
}

export interface OrderCorrectionInvoiceRecoveryCommand {
  diagnosticVersion: string;
  reason: string;
  sendSislog: boolean;
  recoveryToken?: string;
}

export interface OrderCorrectionInvoiceRecoveryPreparation {
  recoveryToken: string;
  expiresAt: string;
}

export interface OrderCorrectionPreview {
  previewId: string;
  orderNumber: number;
  target: OrderCorrectionTargetType | string;
  sourceVersion: string;
  expiresAt: string;
  changedFields: string[];
  current?: OrderCorrectionSourceData | null;
  projected?: OrderCorrectionSourceData | null;
  warnings: string[];
  requiresConfirmation: boolean;
}

export interface OrderCorrectionOperation {
  operationId: string;
  orderNumber: number;
  target: OrderCorrectionTargetType | string;
  status: string;
  invoiceId?: number | null;
  detailsUpdated?: boolean | null;
  transmissionRequested?: boolean | null;
  transmitted?: boolean | null;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  resultReference?: string | null;
  errorCode?: string | null;
  message?: string | null;
  createdAt: string;
  completedAt?: string | null;
  diagnostic?: OrderCorrectionDiagnosticItem | null;
}
